/**
 * 单词训练生成器（7 级）
 * ------------------------------------------------------------
 * 级别设计遵循「输入 → 识别 → 输出 → 记忆」的递进：
 *   L1 听音选词   heard→word   只给发音，四选一
 *   L2 英选中     en→zh        见词选义
 *   L3 中选英     zh→en        见义选词
 *   L4 输入拼写   zh→type      看义打字（有首字母提示）
 *   L5 听音拼写   heard→type   听音打字（无提示）
 *   L6 独立默写   zh→write     看义整词默写（无提示、不计入场次限制）
 *   L7 混合       mixed        以上随机组合
 *
 * 每个级别都产出「标准题目对象」，题型用已有的 choice/fill/spell/listen，
 * 因此判分、错题记录、掌握矩阵都不需要为新级别改代码。
 */
import { registerGenerator } from './registry.js';
import { makeQuestion } from '../../core/models.js';
import { shuffle, sample, pick } from '../../utils/index.js';

export const WORD_LEVELS = [
  { level: 1, name: '听音选词', skill: 'W', icon: '👂', desc: '听发音，从 4 个单词中选出听到的那个' },
  { level: 2, name: '英选中', skill: 'W', icon: '🇬🇧', desc: '看英文单词，选出正确中文意思' },
  { level: 3, name: '中选英', skill: 'W', icon: '🇨🇳', desc: '看中文意思，选出正确英文单词' },
  { level: 4, name: '输入拼写', skill: 'W', icon: '⌨️', desc: '看中文，输入英文单词（给首字母提示）' },
  { level: 5, name: '听音拼写', skill: 'W', icon: '🎧', desc: '只听发音，拼出完整单词（无提示）' },
  { level: 6, name: '独立默写', skill: 'W', icon: '✍️', desc: '看中文，完整默写单词，不允许提示' },
  { level: 7, name: '混合挑战', skill: 'W', icon: '🎲', desc: '以上题型随机混合，检验真实掌握度' },
];

/** 干扰项池：优先同单元其它词，不够时用内置通用词兜底 */
function distractors(word, pool, n, key) {
  const others = pool.filter(w => w.id !== word.id && w[key]);
  const picked = sample(others, n);
  if (picked.length < n) {
    const fallback = ['book', 'pen', 'cat', 'dog', 'red', 'run', 'school', 'apple'];
    for (const f of fallback) {
      if (picked.length >= n) break;
      if (String(word[key]).toLowerCase() === f) continue;
      picked.push({ [key]: f });
    }
  }
  return picked;
}

const base = (word, extra) => ({
  textbookId: word.textbookId,
  unitId: word.unitId,
  refType: 'word',
  refId: word.id,
  origin: 'generated',
  status: 'confirmed',
  ...extra,
});

const builders = {
  /* L1 听音选词 */
  1(word, pool) {
    const others = distractors(word, pool, 3, 'en');
    return makeQuestion(base(word, {
      type: 'listen',
      skill: 'W',
      level: 1,
      prompt: '听发音，选出你听到的单词',
      audio: word.en,
      audioUrl: word.audio || null,   // 内置 MP3（与 en/zh 同一个单词对象，绝不按下标对应）
      options: shuffle([word.en, ...others.map(o => o.en)]),
      answer: [word.en],
      explain: `${word.en} ${word.phonetic || ''} = ${word.zh}`,
      hint: word.phonetic || '',
    }));
  },
  /* L2 英选中 */
  2(word, pool) {
    const others = distractors(word, pool, 3, 'zh');
    return makeQuestion(base(word, {
      type: 'choice',
      skill: 'W',
      level: 2,
      prompt: `“${word.en}” 的中文意思是？`,
      options: shuffle([word.zh, ...others.map(o => o.zh)]),
      answer: [word.zh],
      explain: word.phrase ? `${word.en}：${word.phrase}（${word.phraseZh}）` : `${word.en} = ${word.zh}`,
      hint: word.pos || '',
    }));
  },
  /* L3 中选英 */
  3(word, pool) {
    const others = distractors(word, pool, 3, 'en');
    return makeQuestion(base(word, {
      type: 'choice',
      skill: 'W',
      level: 3,
      prompt: `“${word.zh}” 对应的英文单词是？`,
      options: shuffle([word.en, ...others.map(o => o.en)]),
      answer: [word.en],
      explain: `${word.zh} = ${word.en} ${word.phonetic || ''}`,
      hint: word.en.slice(0, 1) + '...',
    }));
  },
  /* L4 输入拼写（给首字母） */
  4(word) {
    const tip = `${word.en.slice(0, 1)}${'_'.repeat(Math.max(0, word.en.length - 1))}`;
    return makeQuestion(base(word, {
      type: 'spell',
      skill: 'W',
      level: 4,
      prompt: `看中文写出英文单词：${word.zh}`,
      placeholder: tip,
      answer: [word.en],
      explain: `${word.en} ${word.phonetic || ''}`,
      hint: `首字母 ${word.en[0]}，共 ${word.en.replace(/[^a-zA-Z]/g, '').length} 个字母`,
    }));
  },
  /* L5 听音拼写（无提示） */
  5(word) {
    return makeQuestion(base(word, {
      type: 'spell',
      skill: 'W',
      level: 5,
      prompt: '听发音，拼出这个单词',
      audio: word.en,
      audioUrl: word.audio || null,
      answer: [word.en],
      explain: `${word.en} ${word.phonetic || ''} = ${word.zh}`,
      hint: `共 ${word.en.replace(/[^a-zA-Z]/g, '').length} 个字母`,
    }));
  },
  /* L6 独立默写 */
  6(word) {
    return makeQuestion(base(word, {
      type: 'spell',
      skill: 'W',
      level: 6,
      prompt: `默写：${word.zh}`,
      answer: [word.en],
      explain: `${word.en} ${word.phonetic || ''}`,
      hint: '',
    }));
  },
};

/** L7 混合：随机抽取 1~6 级中的一种 */
function build7(word, pool) {
  const level = pick([1, 2, 3, 4, 5, 6]);
  const q = builders[level](word, pool);
  return { ...q, level: 7, mixedFrom: level, prompt: q.prompt };
}

/**
 * build({ content, level, count })
 * @param {object} content  getUnitContent() 的返回值
 * @param {number} level    1..7
 * @param {number} count    期望题量
 * @param {object} opts     { priority: Set<wordId> }
 */
function build({ content, level = 1, count = 10, priority = null } = {}) {
  const pool = content.words || [];
  if (!pool.length) return [];

  let words = priority?.size
    ? [...pool.filter(w => priority.has(w.id)), ...shuffle(pool.filter(w => !priority.has(w.id)))]
    : shuffle(pool);

  // 循环取词直到达到题量（同一单词可在不同级别重复出现）
  const list = [];
  let i = 0;
  while (list.length < count && i < count * 3) {
    list.push(words[i % words.length]);
    i++;
  }

  return list
    .map(word => {
      if (level === 7) return build7(word, pool);
      const fn = builders[level];
      return fn ? fn(word, pool) : null;
    })
    .filter(Boolean)
    .slice(0, count);
}

export const wordGenerator = registerGenerator({
  id: 'word-levels',
  module: 'word',
  title: '单词训练',
  levels: WORD_LEVELS,
  build,
});

export default wordGenerator;
